<template>
  <b-modal
    id="atm_modal_installment"
    hide-header-close
    hide-footer
    scrollable
    centered
    size="lg"
    @hidden="onHidden"
  >
    <template #modal-title>
      <h5 class="atm-title">
        {{ $t('Em quantas vezes deseja pagar?') }}
      </h5>
    </template>

    <b-container>
      <b-row
        v-for="option in stInstallmentOptions"
        :key="option.installments"
      >
        <b-col class="mb-1">
          <e-button
            :id="`atm_modal_installment-btn_${option.installments}`"
            class="atm-btn"
            variant="primary"
            :text="optionLabel(option)"
            @click="onConfirm(option)"
          />
        </b-col>
      </b-row>

      <!--
        Desistir daqui não custa nada: a transação ainda não foi enviada ao pinpad. O cliente volta
        para a escolha da forma de pagamento.
      -->
      <b-row>
        <b-col class="mt-1">
          <e-button
            id="atm_modal_installment-btn_cancel"
            class="atm-btn"
            variant="danger"
            :text="$t('Cancelar')"
            @click="onCancel"
          />
        </b-col>
      </b-row>
    </b-container>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { BModal, BContainer, BRow, BCol } from 'bootstrap-vue'
import EButton from '@/views/components/EButton.vue'

/**
 * Resultado de `showModal` quando o cliente desiste do parcelamento.
 *
 * É um valor próprio, e não `null`, porque os dois desfechos levam a lugares opostos: nulo significa
 * "não há parcelamento a oferecer, siga o pagamento à vista", e este significa "o cliente voltou
 * atrás, não cobre nada".
 */
export const INSTALLMENT_SELECTION_CANCELLED = 'INSTALLMENT_SELECTION_CANCELLED'

export default {
  components: { BModal, BContainer, BRow, BCol, EButton },

  data() {
    return {
      /** Resolve a promessa devolvida por `showModal`. Nulo fora de uma escolha em andamento. */
      resolveSelection: null,
    }
  },

  computed: {
    ...mapGetters('pages/pdv/payBoxInstallment', {
      stInstallmentOptions: 'installmentOptions',
      stHasInstallmentOptions: 'hasInstallmentOptions',
      stDefaultMode: 'defaultMode',
    }),
  },

  methods: {
    ...mapActions('pages/pdv/payBoxInstallment', {
      stFetchInstallmentOptions: 'fetchInstallmentOptions',
    }),

    /**
     * Rótulo da opção.
     *
     * Montado com template literal, e não com interpolação do `$t`: neste projeto o `$t` só
     * interpola quando a chave existe nos arquivos de tradução — texto cru como chave volta com os
     * marcadores `{}` intactos.
     *
     * Duas casas decimais explícitas porque o filtro `currency` passa a exibir quatro assim que o
     * valor tem mais de duas, e o rateio das parcelas produz exatamente esse caso.
     */
    optionLabel(option) {
      const currency = value => this.$options.filters.currency(value, null, 2)

      if (Number(option.installments) <= 1) {
        return `${this.$t('À vista')} — ${currency(option.chargedValue)}`
      }

      return `${option.installments}x ${this.$t('de')} ${currency(
        option.installmentValue
      )} — ${this.$t('total')} ${currency(option.chargedValue)}`
    },

    /**
     * Pergunta ao cliente em quantas vezes pagar.
     *
     * Devolve nulo — sem exibir nada — quando não há parcelamento disponível para o valor. No ATM
     * quem opera é o próprio cliente: uma tela com uma opção só seria um passo a mais para chegar
     * ao mesmo lugar.
     *
     * Devolve <c>INSTALLMENT_SELECTION_CANCELLED</c> quando o cliente desiste.
     */
    async showModal(saleValue) {
      await this.stFetchInstallmentOptions({ saleValue })

      if (!this.stHasInstallmentOptions) {
        return null
      }

      this.$bvModal.show('atm_modal_installment')

      return new Promise(resolve => {
        this.resolveSelection = resolve
      })
    },

    onConfirm(option) {
      this.finish({ ...option, installmentMode: this.stDefaultMode })
    },

    onCancel() {
      this.finish(INSTALLMENT_SELECTION_CANCELLED)
    },

    /**
     * Fechamento por qualquer via que não seja um dos botões — clique fora, ESC — também termina a
     * escolha. Sem isso a promessa ficaria pendente para sempre e o fluxo de pagamento do ATM
     * pararia sem nada na tela explicando por quê.
     */
    onHidden() {
      this.finish(INSTALLMENT_SELECTION_CANCELLED)
    },

    /**
     * Encerra a escolha em andamento.
     *
     * O resolvedor é limpo ANTES de esconder o modal: esconder dispara `hidden`, que cai aqui de
     * novo, e sem isso a segunda passagem tentaria resolver uma promessa já resolvida.
     */
    finish(result) {
      const resolve = this.resolveSelection
      this.resolveSelection = null

      this.$bvModal.hide('atm_modal_installment')

      if (resolve) {
        resolve(result)
      }
    },
  },
}
</script>

<style scoped>
.atm-title {
  font-size: 1.25rem;
}

.atm-btn {
  width: 100%;
  padding: 0.8rem 2rem;
  font-size: 1.25rem;
  line-height: 37px;
}
</style>
